import {Observable, OperatorFunction, Subscription} from "rxjs";

/**
 * exhaustMapLatest - coalescing projection for "save the latest snapshot" style requests.
 *
 * Behaves like exhaustMap while an inner Observable is in flight, except that the LATEST source value which
 * arrived meanwhile is projected as soon as the inner completes (older ones are dropped). A value that arrives
 * while nothing is in flight is projected at once, never time deferred.
 *
 * Guarantees:
 * - never two inners at once, so requests cannot overtake each other on the server (no stale overwrite)
 * - bursts collapse to at most two requests: the one in flight and one carrying the latest snapshot
 * - a lone value is projected synchronously, exactly like switchMap / mergeMap would
 *
 * @param project maps a source value to the inner Observable (typically an HTTP request)
 * @param flushWhen optional predicate; a value it accepts cancels the inner in flight, drops any pending value
 *                  and is projected immediately (used for a logout push that must leave before navigation)
 */
export function exhaustMapLatest<T, R>(project: (value: T) => Observable<R>, flushWhen?: (value: T) => boolean): OperatorFunction<T, R> {
    return (source: Observable<T>) => new Observable<R>((subscriber) => {
        let inner: Subscription | null = null;
        let pending: T;
        let hasPending = false;
        let sourceDone = false;

        const run = (value: T) => {
            inner = project(value).subscribe({
                next: (result) => subscriber.next(result),
                error: (e) => subscriber.error(e),
                complete: () => {
                    inner = null;
                    if (hasPending) {
                        const next = pending;
                        hasPending = false;
                        pending = undefined;
                        run(next);
                    } else if (sourceDone) {
                        subscriber.complete();
                    }
                }
            });
        };

        const outer = source.subscribe({
            next: (value) => {
                if (flushWhen && flushWhen(value)) {
                    if (inner) {
                        inner.unsubscribe();
                        inner = null;
                    }
                    hasPending = false;
                    pending = undefined;
                    run(value);
                    return;
                }
                if (inner) {
                    pending = value;
                    hasPending = true;
                    return;
                }
                run(value);
            },
            error: (e) => subscriber.error(e),
            complete: () => {
                sourceDone = true;
                if (!inner) subscriber.complete();
            }
        });

        return () => {
            outer.unsubscribe();
            if (inner) inner.unsubscribe();
        };
    });
}
